import { captureException } from '@sentry/react';
import { getViewerId } from 'components/Scene/helpers/scene';
import { configurationAction } from 'redux-store/configuration/configuration.store';
import store from 'redux-store/store';
import { PANELS_PER_DOOR_FRAME, VIEWER_DEFAULT_ALPHA_INDEX } from 'utils/constants';
import { SCENE_COLORS } from 'utils/constants/colors';
import getAssetsPath from 'utils/getAssetsPath';

import { CustomOutlineComponent } from 'CoreAPI/Components/CustomOutlineComponent';
import type { PanelComponent } from 'CoreAPI/Components/PanelComponent';
import type { RailComponent } from 'CoreAPI/Components/RailComponent';
import { RailTypeEnum } from 'CoreAPI/Components/RailTypeEnum';
import { SkyttaComponentIds } from 'CoreAPI/Components/SkyttaComponentIds';
import type { RoomDimensionOptionsComponent } from 'CoreAPI/Components/UserChoicesComponents/RoomDimensionOptionsComponent';
import { isSelectedPanelsPossibleToCut } from 'CoreAPI/coreSceneQueries';
import { SystemBase3 } from 'CoreAPI/systembases/SystemBase3';
import { getCamera } from 'CoreAPI/viewerSetup';
import { type IViewer, ObjectHandle, RENDER_LAYER_IGNORE_RAYCAST } from '@inter-ikea-betrakta/viewer';
import { PrimitiveBuilder } from '@inter-ikea-betrakta/viewer/dist/modules/PrimitiveBuilder';
import { Room } from '@inter-ikea-betrakta/viewer/dist/modules/Room';
import {
  type Component,
  ComponentIds,
  type Core,
  type CoreServices,
  type Entity,
  type LoadedModelInfoComponent,
  MeasurementUtils,
  type RoomGeometryComponent,
  TransformUtils,
  Vector,
  type ViewerObjectComponent
} from '@inter-ikea-core/core';

export class ViewerRoomComponent implements Component {
  public static ID = SkyttaComponentIds.ViewerRoomComponentId;
  public readonly typeId = ViewerRoomComponent.ID;
  public readonly viewerRoom: Room;
  public readonly doStoreInSnapshot: boolean = true;
  public readonly ignoreEntitiesWithComponents: number[] | null = null;

  constructor(room: Room) {
    this.viewerRoom = room;
  }

  public eq(_other: Component) {
    return false;
  }
}

const invisibleMaterial = (viewer: IViewer) => {
  const outlineObjectMaterial = viewer.create_material();
  viewer.set_opacity(outlineObjectMaterial, 0);
  viewer.set_roughness(outlineObjectMaterial, 0);
  viewer.set_metallic(outlineObjectMaterial, 0);
  viewer.set_unlit(outlineObjectMaterial, true);

  return outlineObjectMaterial;
};

export class RoomRenderingSystem extends SystemBase3<RoomDimensionOptionsComponent, ViewerRoomComponent, LoadedModelInfoComponent> {
  public readonly id: string = 'RoomRenderingSystem';
  public readonly systemsBeforeMe: string[] | null = null;
  public readonly systemsAfterMe: string[] | null = null;
  public readonly doingChangesOnComponent: number[] = [ViewerRoomComponent.ID];
  public boxIds: number[] = [];
  public viewerRoom: Room | undefined;

  private core: Core;
  private coreServices: CoreServices;
  private viewer: IViewer;
  private outlineObjectMaterial: number;
  private primitiveBuilder: PrimitiveBuilder;

  constructor(core: Core, viewer: IViewer) {
    super(SkyttaComponentIds.RoomDimensionOptionsComponentId, ViewerRoomComponent.ID, ComponentIds.LOADED_MODEL_INFO_COMPONENT_ID);
    this.core = core;
    this.coreServices = this.core.services;
    this.viewer = viewer;
    this.recreateRoom();
    this.boxIds = [];
    this.outlineObjectMaterial = invisibleMaterial(this.viewer);
    this.primitiveBuilder = new PrimitiveBuilder(this.viewer);
  }

  public recreateRoom() {
    const room = this.coreServices.sceneManager.entities.find((e) => e.hasComponent(ComponentIds.ROOM_GEOMETRY_COMPONENT_ID));
    if (room && this.viewer) {
      const roomGeometry = room.getComponent<RoomGeometryComponent>(ComponentIds.ROOM_GEOMETRY_COMPONENT_ID)!;

      // We need to get the dimensions from RoomDimensionOptionsComponent so that we update the room with the user input instead of core value.
      // This is so that the ceiling height will render lower than the actually room is in core, if the room is lower than the doors are.
      const roomDimensions = this.coreServices.sceneManager.entities
        .find((e) => e.hasComponent(SkyttaComponentIds.RoomDimensionOptionsComponentId))
        ?.getComponent<RoomDimensionOptionsComponent>(SkyttaComponentIds.RoomDimensionOptionsComponentId);

      const roomSize = new Vector(
        roomDimensions?.width ?? roomGeometry.dimensions.size.X,
        roomDimensions?.height ?? roomGeometry.dimensions.size.Y,
        roomGeometry.dimensions.size.Z
      );

      this.updateRoom(room, roomSize);
    }
  }

  public onAddComponent(_entity: Entity, roomDimensions: RoomDimensionOptionsComponent): void {
    const roomSize = new Vector(roomDimensions.width, roomDimensions.height, roomDimensions.depth);
    const room = this.coreServices.sceneManager.entities.find((e) => e.hasComponent(ComponentIds.ROOM_GEOMETRY_COMPONENT_ID));
    if (room) {
      this.updateRoom(room, roomSize);
    }
  }

  public onUpdateComponent(_entity: Entity, _oldSize: RoomDimensionOptionsComponent, newSize: RoomDimensionOptionsComponent): void {
    const roomSize = new Vector(newSize.width, newSize.height, newSize.depth);
    const room = this.coreServices.sceneManager.entities.find((e) => e.hasComponent(ComponentIds.ROOM_GEOMETRY_COMPONENT_ID));
    if (room) {
      this.updateRoom(room, roomSize);
    }
  }

  public onRemoveComponent(_entity: Entity, _roomDimensions: RoomDimensionOptionsComponent): void {}

  protected onAddComponent2(_entity: Entity, _newComponent: ViewerRoomComponent): void {}
  protected onUpdateComponent2(_entity: Entity, _oldComponent: ViewerRoomComponent, _newComponent: ViewerRoomComponent): void {}
  protected onRemoveComponent2(_room: Entity, oldComponent: ViewerRoomComponent): void {
    this.disposeRoom(oldComponent);
  }

  private createOutlineObject = async (parentId: number, viewer: IViewer) => {
    const bounds = await viewer.get_object_bounds_world(parentId);

    const { max, min } = bounds;

    const dimension = {
      width: max.x - min.x,
      height: max.y - min.y,
      depth: max.z - min.z
    };

    const outlineObjectId = this.primitiveBuilder.create_box(dimension.width, dimension.height, dimension.depth, {
      material: this.outlineObjectMaterial
    });

    viewer.add_to_layer(outlineObjectId, RENDER_LAYER_IGNORE_RAYCAST);

    viewer.set_parent(outlineObjectId, parentId);

    return outlineObjectId;
  };

  protected onAddComponent3(): void {}

  private doRailUpdate(entity: Entity) {
    const railComponent = entity.getComponent<RailComponent>(SkyttaComponentIds.RailComponentId);
    if (railComponent?.railType === RailTypeEnum.TopRail) {
      this.moveTopRail(entity);
    }
  }

  private async doDoorFrameUpdate(entity: Entity) {
    this.addBox(entity);

    const viewerId = getViewerId(entity);

    if (!viewerId) {
      return;
    }

    const outlineId = await this.createOutlineObject(viewerId, this.viewer);

    entity.addComponent(new CustomOutlineComponent(outlineId));
  }

  private doTopPanelUpdate() {
    const doorFrames = this.coreServices.sceneManager.entities.filter((entity: Entity) =>
      entity.hasComponent(SkyttaComponentIds.SlidingDoorFrameComponentId)
    );
    doorFrames.forEach((doorFrame) => {
      this.addBox(doorFrame);
    });
  }

  protected async onUpdateComponent3(entity: Entity, _oldComponent: LoadedModelInfoComponent, newComponent: LoadedModelInfoComponent): Promise<void> {
    if (!newComponent.hasFinishedLoading) {
      return;
    }

    if (entity.hasComponent(SkyttaComponentIds.RailComponentId)) {
      this.doRailUpdate(entity);
      return;
    }

    if (entity.hasComponent(SkyttaComponentIds.SlidingDoorFrameComponentId)) {
      this.doDoorFrameUpdate(entity);
      return;
    }

    if (entity.getComponent<PanelComponent>(SkyttaComponentIds.PanelComponentId)?.verticalPositionInDoorFrame === PANELS_PER_DOOR_FRAME) {
      this.doTopPanelUpdate();
      return;
    }
  }

  protected onRemoveComponent3(entity: Entity): void {
    const isDoorFrameUpdate = entity.hasComponent(SkyttaComponentIds.SlidingDoorFrameComponentId);
    if (isDoorFrameUpdate) {
      entity.removeComponent(SkyttaComponentIds.CustomOutlineComponentId);
    }
  }

  private howMuchToCut() {
    const roomGeometry = this.coreServices.sceneManager.entities
      .find((e) => e.hasComponent(ComponentIds.ROOM_GEOMETRY_COMPONENT_ID))
      ?.getComponent<RoomGeometryComponent>(ComponentIds.ROOM_GEOMETRY_COMPONENT_ID);

    const roomDimensions = this.coreServices.sceneManager.entities
      .find((e) => e.hasComponent(SkyttaComponentIds.RoomDimensionOptionsComponentId))
      ?.getComponent<RoomDimensionOptionsComponent>(SkyttaComponentIds.RoomDimensionOptionsComponentId);

    if (!roomGeometry || !roomDimensions) {
      return; // no need to move
    }

    const diff = roomGeometry.dimensions.size.Y - roomDimensions.height;

    if (diff <= 0) {
      return; // no need to cut
    }

    return diff;
  }

  private moveTopRail(entity: Entity) {
    const diff = this.howMuchToCut();

    if (!diff) return;

    const oldRailPosition = TransformUtils.worldPosition(entity);
    const newRailPosition = oldRailPosition.plus(new Vector(0, -diff, 0));
    const model = entity.getComponent<ViewerObjectComponent>(ComponentIds.VIEWER_OBJECT_COMPONENT_ID)?.viewerObject;
    model?.setPosition(newRailPosition);
  }

  private addBox(doorFrameEntity: Entity) {
    const diff = this.howMuchToCut();
    if (!diff) return;

    const doorFrameSize = MeasurementUtils.getOutsideMeasurementSingle(doorFrameEntity);

    if (!doorFrameSize) return;

    const model = doorFrameEntity.getComponent<ViewerObjectComponent>(ComponentIds.VIEWER_OBJECT_COMPONENT_ID)?.viewerObject;
    const model_id = model?.getViewerObjectId();

    if (!model_id) return;

    // Delete old boxes.
    this.viewer.get_children(model_id).forEach((child) => {
      if (this.boxIds.includes(child)) {
        this.viewer.delete_object(child);
        this.boxIds = this.boxIds.filter((value) => value !== child);
      }
    });

    // Create box!
    const transparent_material = this.viewer.create_material();

    const { blue, red } = SCENE_COLORS;

    const color = isSelectedPanelsPossibleToCut(this.core) ? blue : red;

    this.viewer.set_base_color(transparent_material, color);
    this.viewer.set_roughness(transparent_material, 0.7);
    this.viewer.set_metallic(transparent_material, 0.0);
    this.viewer.set_opacity(transparent_material, 0.5);

    const oversize = 12;

    const size = {
      X: (doorFrameSize.width + oversize) / 1000,
      Y: (diff + oversize) / 1000,
      Z: (doorFrameSize.depth + oversize) / 1000
    };

    const box_handle = new ObjectHandle(
      this.primitiveBuilder.create_box(size.X, size.Y, size.Z, {
        material: transparent_material
      }),
      this.viewer
    );

    this.boxIds.push(box_handle.id);

    this.viewer.set_receive_shadow(box_handle.id, false);
    this.viewer.set_cast_shadow(box_handle.id, false);
    this.viewer.set_parent(box_handle.id, model_id);

    const boxPosition = {
      X: 0,
      Y: doorFrameSize.height / 1000 / 2 - diff / 1000 / 2 + oversize / 2 / 1000,
      Z: 0
    };

    this.viewer.set_position(box_handle.id, boxPosition.X, boxPosition.Y, boxPosition.Z);

    const isCameraBehindDoors = getCamera().position.z < 0;

    this.viewer.set_alpha_sorting_index(box_handle.id, VIEWER_DEFAULT_ALPHA_INDEX + (isCameraBehindDoors ? -1 : 1));
  }

  private updateRoom(roomEntity: Entity, roomSize: Vector): void {
    // Check that this really is a room in the scene
    if (!roomEntity.hasComponent(ComponentIds.IN_SCENE_COMPONENT_ID)) {
      return;
    }
    if (!this.viewer) {
      return;
    }

    const { X, Y, Z } = roomSize;

    const roomComponent = roomEntity.getComponent<ViewerRoomComponent>(ViewerRoomComponent.ID);
    if (roomComponent) {
      this.disposeRoom(roomComponent);
    }
    const room = this.createRoomInViewer(this.viewer, X / 1000, Y / 1000, Z / 1000);
    this.coreServices.entityManager.addComponent(roomEntity, new ViewerRoomComponent(room));
  }

  private disposeRoom(roomComponent: ViewerRoomComponent) {
    roomComponent.viewerRoom.dispose();
  }

  /**
   * Assumes all measurements in meters
   */
  private createRoomInViewer(viewer: IViewer, width: number, height: number, depth: number): Room {
    // Create IPEX viewer wall vertices for the core room.
    const wall_vertices = [
      { x: -width / 2, y: 0, z: 0, w: height },
      { x: width / 2, y: 0, z: 0, w: height },
      { x: width / 2, y: 0, z: depth, w: height },
      { x: -width / 2, y: 0, z: depth, w: height }
    ];

    this.viewerRoom = new Room(viewer, {
      wall_vertices,
      windows: [],
      doors: [],
      ceiling_light_color: { r: 1.0, g: 1.0, b: 1.0 },
      ceiling_light_intensity: 2,
      floor_light_color: { r: 1.0, g: 1.0, b: 1.0 },
      floor_light_intensity: 3
    });

    // Define transparent material
    const ceilingMaterial = viewer.create_material();
    viewer.set_base_color(ceilingMaterial, { r: 1, g: 1, b: 1 });
    viewer.set_roughness(ceilingMaterial, 0.7);
    viewer.set_metallic(ceilingMaterial, 0.0);
    viewer.set_opacity(ceilingMaterial, 0.0);

    const roomMaterials = [
      {
        url: getAssetsPath('./room/wall/wall_lowres.gltf'),
        material: viewer.load_material(getAssetsPath('./room/wall/wall_lowres.gltf'))
      },
      {
        url: getAssetsPath('./room/skirting/skirting.gltf'),
        material: viewer.load_material(getAssetsPath('./room/skirting/skirting.gltf'))
      },
      {
        url: getAssetsPath('./room/floor/floor-embeded_lowres.glb'),
        material: viewer.load_material(getAssetsPath('./room/floor/floor-embeded_lowres.glb'))
      }
    ];

    const [wallMaterial, skirtingMaterial, floorMaterial] = roomMaterials;

    this.viewerRoom.set_material(
      wallMaterial.material,
      skirtingMaterial.material,
      floorMaterial.material,
      // viewer.load_material(getAssetsPath("./room/ceiling/ceiling_new.gltf"))
      // we use this instead to make the ceiling transparent
      ceilingMaterial
    );

    const { dispatch } = store;

    roomMaterials.forEach((value) => {
      viewer.material_ready(value.material).catch(() => {
        captureException(new Error('Failed to load critical room material.'), (scope) => {
          return scope.setExtra('materialUrl', value.url);
        });
        dispatch(configurationAction.setShowErrorPage(true));
      });
    });

    this.viewerRoom.get_room_objects().forEach((value) => {
      viewer.set_receive_shadow(value, true);
    });
    this.viewerRoom.async_calculate_lighting();
    return this.viewerRoom;
  }
}
